import { apiGet, mutate } from "./client";

const BASE = "/api/orchestrator/users";

/**
 * An account as the server is willing to describe it.
 *
 * No password or secret field exists here because none is ever sent: the server
 * projects the row rather than redacting it. `has_password` and
 * `has_client_credential` are what is left — enough to see how an account signs
 * in, and nothing that helps anyone sign in as them.
 */
export interface ConsoleUser {
  user_id: string;
  username: string;
  email: string;
  full_name: string | null;
  tenants: string[];
  role: "admin" | "member" | string;
  is_active: boolean;
  has_password: boolean;
  has_client_credential: boolean;
  client_id: string | null;
  /** First few characters of the secret, for matching a leak back to an account. */
  client_secret_prefix: string | null;
  client_secret_issued_at: string | null;
  /**
   * Whether "Show secret" can work: a key is configured and this account has a
   * client credential. Still not a guarantee — a secret issued before the
   * stored copy existed reports SECRET_NOT_RECOVERABLE when actually read.
   */
  client_secret_viewable: boolean;
  has_api_key: boolean;
  /** The key's first characters, for matching a leaked key back to its account. */
  api_key_prefix: string | null;
  api_key_issued_at: string | null;
  has_public_key: boolean;
  /** `SHA256:…`, the same form `ssh-keygen -lf` prints, so a holder can compare. */
  public_key_fingerprint: string | null;
  public_key_added_at: string | null;
  /** Every method's effective scope, defaults filled in. */
  api_access: Record<AccessMethod, Scope>;
  last_login_at: string | null;
  last_token_at: string | null;
  created_by: string | null;
  created_at: string | null;
}

export type AccessMethod = "password" | "client_secret" | "api_key" | "key_pair";

/** "all", or the module keys a credential may reach. */
export type Scope = "all" | string[];

export interface ApiModule {
  key: string;
  label: string;
  description: string;
}

export interface ApiMethod {
  key: AccessMethod;
  label: string;
  default_scope: Scope;
}

export interface UserListResponse {
  status: string;
  users: ConsoleUser[];
  total: number;
  /** Active administrators, so the UI can refuse to offer the last demotion. */
  admin_count: number;
  /** Null for an .env operator, who has no row of their own. */
  caller_user_id: string | null;
  /** What the account editor offers; the server enforces the same lists. */
  api_modules: ApiModule[];
  api_methods: ApiMethod[];
}

/** Only ever returned by the call that mints it. Not recoverable afterwards. */
export interface SecretResult {
  user: ConsoleUser;
  client_secret: string;
}

export interface ApiKeyResult {
  user: ConsoleUser;
  api_key: string;
}

export const userListPath = (includeInactive: boolean): string =>
  `${BASE}?include_inactive=${includeInactive ? "true" : "false"}`;

export interface CreateUserInput {
  username: string;
  email: string;
  password?: string;
  full_name?: string;
  tenants: string[];
  /** A role name from the roles table; the server refuses one it does not hold. */
  role: string;
  with_client_secret: boolean;
  with_api_key?: boolean;
  /** PEM. Validated server-side; a private key is refused, not stored. */
  public_key?: string;
  api_access?: Partial<Record<AccessMethod, Scope>>;
}

export interface CreateUserResult {
  user: ConsoleUser;
  client_secret: string | null;
  api_key: string | null;
}

export const createUser = (input: CreateUserInput) =>
  mutate<CreateUserResult>(BASE, "POST", input);

export interface UserPatch {
  full_name?: string;
  email?: string;
  tenants?: string[];
  role?: string;
  is_active?: boolean;
  api_access?: Partial<Record<AccessMethod, Scope>>;
}

export const updateUser = (id: string, patch: UserPatch) =>
  mutate<{ user: ConsoleUser }>(`${BASE}/${encodeURIComponent(id)}`, "PATCH", patch);

/** Stops the client secret working; the client ID stays. */
export const withdrawSecret = (id: string) =>
  mutate<{ user: ConsoleUser }>(`${BASE}/${encodeURIComponent(id)}/secret`, "DELETE");

/** Issues a NEW API key and invalidates the current one. Shown once. */
export const issueApiKey = (id: string) =>
  mutate<ApiKeyResult>(`${BASE}/${encodeURIComponent(id)}/api-key`, "POST");

export const revokeApiKey = (id: string) =>
  mutate<{ user: ConsoleUser }>(`${BASE}/${encodeURIComponent(id)}/api-key`, "DELETE");

export const setPublicKey = (id: string, public_key: string) =>
  mutate<{ user: ConsoleUser }>(`${BASE}/${encodeURIComponent(id)}/public-key`, "PUT", {
    public_key,
  });

export const removePublicKey = (id: string) =>
  mutate<{ user: ConsoleUser }>(`${BASE}/${encodeURIComponent(id)}/public-key`, "DELETE");

export const setUserRole = (id: string, role: string) =>
  mutate<{ user: ConsoleUser }>(`${BASE}/${encodeURIComponent(id)}`, "PATCH", {
    role,
  });

export const setUserActive = (id: string, is_active: boolean) =>
  mutate<{ user: ConsoleUser }>(`${BASE}/${encodeURIComponent(id)}`, "PATCH", {
    is_active,
  });

/** Issues a NEW secret and invalidates the current one. */
export const rotateSecret = (id: string) =>
  mutate<SecretResult>(`${BASE}/${encodeURIComponent(id)}/secret`, "POST");

/** Reads the existing secret. Changes nothing — note GET, not POST. */
export const showSecret = (id: string) =>
  apiGet<SecretResult>(`${BASE}/${encodeURIComponent(id)}/secret`);

export interface PasswordResult {
  user: ConsoleUser;
  /** Only set when the server generated it; null for a password you supplied. */
  password: string | null;
}

export const setUserPassword = (id: string, password: string | null) =>
  mutate<PasswordResult>(`${BASE}/${encodeURIComponent(id)}/password`, "POST", {
    password,
  });

/** Has the server invent a strong password and return it once. */
export const generateUserPassword = (id: string) =>
  mutate<PasswordResult>(`${BASE}/${encodeURIComponent(id)}/password`, "POST", {
    generate: true,
  });
